/* eslint-disable max-lines */
import { NgTemplateOutlet } from '@angular/common';
import {
  forwardRef,
  OnChanges,
  OnDestroy,
  OnInit,
  Provider,
  SimpleChanges,
  TemplateRef,
  Type,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Renderer2,
  signal,
  ViewChild,
  output,
  inject,
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { DOMParser as PMDOMParser } from 'prosemirror-model';
import type { EditorView } from 'prosemirror-view';
import { BehaviorSubject, defer, shareReplay, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';

import type {
  ConfigPlugins,
  EditorButton,
  EditorEvent,
  IProsemirrorConfig,
  JsonMark,
  JsonNode,
  PluginsAPI,
  PluginsEvents,
} from '@core/prosemirror-legacy';
import { EditorMode, isEmptyDoc, PendingEnd, PendingStart } from '@core/prosemirror-legacy';
import {
  EditorView$,
  OVERLAY_PROVIDERS,
  ProsemirrorService,
  StateUpdated$,
} from '@core/prosemirror-legacy/angular';
import type { EditorJSONState } from '@core/prosemirror-types';
import { SymbolPickerService } from '@core/symbol-legacy';
import { DialogService, SpinnerComponent } from '@core/user-interface-legacy';

import { ProsemirrorConfig, SCROLL_PROVIDER } from '../../providers';
import { WysiwygToastService } from '../../services/wysiwyg-toast.service';
import { AlignMenuService } from '../align-menu/align-menu.service';
import {
  colorMenuServiceProvider,
  PM_COLOR_SERVICE_FOR_TEXT_BACKGROUND,
  PM_COLOR_SERVICE_FOR_TEXT_COLOR,
} from '../color-menu/color-menu.service';
import { ColumnsMenuService } from '../columns-menu/columns-menu.service';
import { FullScreenService } from '../full-screen/full-screen.service';
import { HeadingMenuService } from '../heading-menu/heading-menu.service';
import { HtmlClassMenuService } from '../html-class-menu/html-class-menu.service';
import { ImageMenuService } from '../image/image-menu/image-menu.service';
import { LengthCounterService } from '../length-counter/length-counter.service';
import { ListMenuService } from '../list-menu/list-menu.service';
import { GroupedMentionsListService } from '../mentions-strategy/grouped-mentions/grouped-mentions-list.service';
import { MentionsListService } from '../mentions-strategy/mentions/mentions-list.service';
import { PlaceholderDropdownService } from '../placeholder/placeholder-dropdown/placeholder-dropdown.service';
import { SchedulerLinksDropdownService } from '../scheduler-links/scheduler-links-dropdown/scheduler-links-dropdown.service';
import { SizeMenuService } from '../size-menu/size-menu.service';
import { PmToolbarComponent } from '../toolbar/toolbar.component';
import { ToolbarService } from '../toolbar/toolbar.service';
import { ContextToolbarService } from '../tooltips/context-toolbar/context-toolbar.service';
import {
  figureContextMenuServiceProvider,
  PM_CONTEXT_MENU_SERVICE_IMAGE,
  PM_CONTEXT_MENU_SERVICE_VIDEO,
} from '../tooltips/figure-context-menu/figure-context-menu.service';
import { LinkComponentService } from '../tooltips/link/link-component.service';
import { VideoMenuService } from '../video/video-menu.service';
import { VideoParserService } from '../video/video-parser.service';

const WYSIWYG_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => WysiwygComponent),
  multi: true,
};

@Component({
  selector: 'rt-wysiwyg',
  templateUrl: './wysiwyg.component.html',
  styleUrls: ['./wysiwyg.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ProsemirrorService,
    ContextToolbarService,
    StateUpdated$,
    ToolbarService,
    HeadingMenuService,
    VideoMenuService,
    VideoParserService,
    SizeMenuService,
    ColumnsMenuService,
    AlignMenuService,
    ImageMenuService,
    ListMenuService,
    LinkComponentService,
    HtmlClassMenuService,
    MentionsListService,
    GroupedMentionsListService,
    PlaceholderDropdownService,
    SchedulerLinksDropdownService,
    SymbolPickerService,
    LengthCounterService,
    EditorView$,
    WysiwygToastService,
    WYSIWYG_VALUE_ACCESSOR,
    OVERLAY_PROVIDERS,
    SCROLL_PROVIDER,
    colorMenuServiceProvider(PM_COLOR_SERVICE_FOR_TEXT_COLOR),
    colorMenuServiceProvider(PM_COLOR_SERVICE_FOR_TEXT_BACKGROUND),
    figureContextMenuServiceProvider(PM_CONTEXT_MENU_SERVICE_IMAGE),
    figureContextMenuServiceProvider(PM_CONTEXT_MENU_SERVICE_VIDEO),
    {
      provide: FullScreenService,
      deps: [ElementRef, DialogService],
    },
  ],
  host: {
    tabindex: '0',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut($event)',
    '[class.no-border]': '!withBorder',
    '[class.min-height]': '!!withMinHeight',
    '[class.sticky-toolbar]': '!!hasStickyToolbar',
  },
  imports: [PmToolbarComponent, NgTemplateOutlet, SpinnerComponent],
})
export class WysiwygComponent<C extends IProsemirrorConfig>
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  private readonly prosemirrorService = inject<ProsemirrorService<C>>(ProsemirrorService);
  private readonly toolbarService = inject(ToolbarService);
  private readonly stateUpdated$ = inject(StateUpdated$);
  private readonly elRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly prosemirrorConfig = inject(ProsemirrorConfig, { optional: true })!;
  private readonly toastService = inject(WysiwygToastService);
  public readonly cdRef = inject(ChangeDetectorRef);

  public editorView: EditorView;

  @Input() public config: Type<C> = <Type<C>>this.prosemirrorConfig;
  @Input() public withBorder = true;
  @Input() public withMinHeight = false;
  @Input() public toolbarVisible = true;
  @Input() public toolbarRightVisible = false;
  @Input() public footerTmpl: TemplateRef<{ toolbar: TemplateRef<void> }>;
  @Input() public toolbarButtonWrapperTemplate?: TemplateRef<any>;
  @Input() public autofocus: boolean = false;
  @Input() public disabled: boolean;
  @Input() public allowEmptyDoc: boolean = false;
  @Input() public hasStickyToolbar: boolean = false;

  @Input() public placeholder: string;
  @Input() public actionButtons: EditorButton[] = [];

  public focusChange = output<boolean>();
  public editorEvents = output<PluginsEvents<ConfigPlugins<C>>>();

  @ViewChild('editor', { static: true })
  public editorElement: ElementRef<HTMLElement>;

  @ViewChild('toolbarContent', { static: true })
  public toolbarTemplate: TemplateRef<void>;

  @ViewChild('toolbarContainer', { read: ElementRef, static: false })
  public toolbarContainer: ElementRef<Element>;

  public editorApi: PluginsAPI<ConfigPlugins<C>>;
  public editorApi$ = new BehaviorSubject<PluginsAPI<ConfigPlugins<C>> | null>(null);
  public stateChanges$ = this.stateUpdated$.pipe(
    distinctUntilChanged((s1, s2) => s1.doc.eq(s2.doc)),
    shareReplay(),
    takeUntil(defer(() => this.ngOnDestroy$)),
  );
  public hasFocus: boolean = false;
  public showSpinner = false;
  public toolbarIsPinned = signal(false);
  public toolbarNoShadow = signal(false);

  private initialValue: EditorJSONState;
  private onChange: (val: any) => void;
  private editorSchema: ReturnType<ProsemirrorService<C>['createEditor']>['schema'];
  private ngOnDestroy$ = new Subject<void>();
  private hideSpinner$ = new Subject<void>();
  private toolbarPinnedObs: IntersectionObserver | null = null;
  private editorScrollsOutObs: IntersectionObserver | null = null;

  public ngOnChanges(changes: SimpleChanges) {
    if ('actionButtons' in changes) {
      this.toolbarService.setAdditionalButtons(this.actionButtons);
    }

    if ('disabled' in changes && this.editorView) {
      this.setDisabledState(this.disabled);
    }
  }

  public ngOnInit(): void {
    const { view, schema, publicApi, eventStream$, sanitizeJSON } =
      this.prosemirrorService.createEditor(this.editorElement.nativeElement, this.config, {
        mode: EditorMode.Wysiwyg,
        placeholder: this.placeholder,
        initialState: this.initialValue,
      });

    eventStream$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((event: any) => {
      this.onEditorEvent(event);
      this.editorEvents.emit(event);
    });

    this.editorView = view;
    this.setDisabledState(this.disabled);
    this.editorSchema = schema;
    this.editorApi = publicApi;
    this.editorApi$.next(publicApi);
    this.toastService.init(eventStream$);

    this.stateUpdated$
      .pipe(
        startWith(view.state),
        distinctUntilChanged((s1, s2) => s1.doc.eq(s2.doc)),
        map(state => {
          const { selection, ...temp } = state.toJSON();
          const jsonState = <EditorJSONState>{
            ...temp,
            doc: this.fixAttrsNoPrototypeNodes(temp.doc),
          };

          return isEmptyDoc(jsonState) && !this.allowEmptyDoc ? null : jsonState;
        }),
        map(jsonState => {
          if (!jsonState || !sanitizeJSON) return jsonState;
          return {
            ...jsonState,
            doc: sanitizeJSON(<JsonNode>jsonState.doc),
          };
        }),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(state => {
        if (!this.onChange) return;

        this.onChange(state);
      });

    if (this.autofocus) {
      this.editorView.focus();
      this.onFocusIn();
    }

    this.hideSpinner$.pipe(debounceTime(300), takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.showSpinner = false;
      this.cdRef.detectChanges();
    });
  }

  public ngAfterViewInit(): void {
    if (this.toolbarVisible && this.hasStickyToolbar) this.bindToolbarObservers();
  }

  public ngOnDestroy() {
    this.cleanObservers();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public registerOnChange(fn: (v: any) => void): void {
    this.onChange = fn;
  }

  public onTouched: Function = () => void 0;

  public registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.editorView.setProps({ editable: () => !isDisabled });
    this.toggleClass('disabled', isDisabled);
  }

  public writeValue(jsonState: EditorJSONState): void {
    if (!this.editorView) {
      this.initialValue = jsonState;
      return;
    }
    this.prosemirrorService.updateState(jsonState);
  }

  public onFocusIn() {
    this.hasFocus = true;
    this.focusChange.emit(this.hasFocus);
  }

  public onFocusOut(event: FocusEvent) {
    const wysiwygElement: HTMLElement = this.elRef.nativeElement;
    const eventSource = <HTMLElement>(event.relatedTarget || event.target);
    const target = eventSource === this.editorView.dom ? null : eventSource;
    if (wysiwygElement.contains(target)) return;

    this.hasFocus = false;
    this.focusChange.emit(this.hasFocus);
    this.onTouched();
  }

  public toggleClass(className: string, isEnabled: boolean) {
    if (isEnabled) {
      this.renderer.addClass(this.elRef.nativeElement, className);
    } else {
      this.renderer.removeClass(this.elRef.nativeElement, className);
    }
  }

  public insertHtml(html: Document) {
    const { state } = this.editorView;
    const { schema, tr } = state;
    const content = PMDOMParser.fromSchema(schema).parse(html);
    if (state.doc.textContent.length === 0) {
      tr.replaceWith(state.selection.from - 1, state.selection.to, content);
    } else {
      tr.insert(state.selection.from, content);
    }
    this.editorView.dispatch(tr);
  }

  private onEditorEvent(event: EditorEvent) {
    if (event instanceof PendingStart) {
      this.showSpinner = true;
      this.cdRef.detectChanges();
    }
    if (event instanceof PendingEnd) {
      this.hideSpinner$.next();
    }
  }

  // TODO: workaround for https://github.com/epoberezkin/fast-deep-equal/issues/49
  private fixAttrsNoPrototypeNodes(node: JsonNode): JsonNode {
    return {
      ...node,
      ...(node.attrs ? { attrs: { ...node.attrs } } : {}),
      ...(node.content
        ? { content: node.content.map(node => this.fixAttrsNoPrototypeNodes(node)) }
        : {}),
      ...(node.marks
        ? {
            marks: node.marks.map(
              mark =>
                <JsonMark>{
                  ...mark,
                  ...(mark.attrs ? { attrs: { ...mark.attrs } } : {}),
                },
            ),
          }
        : {}),
    };
  }

  private bindToolbarObservers() {
    this.observeToolbarPinned();
    this.observeEditorScrollOut();
  }

  private observeToolbarPinned() {
    this.toolbarPinnedObs = new IntersectionObserver(
      entries => {
        const toolbarIsPinned = entries[0].intersectionRatio < 1;
        this.toolbarIsPinned.set(toolbarIsPinned);
      },
      { threshold: [1] },
    );
    this.toolbarPinnedObs.observe(this.toolbarContainer.nativeElement);
  }

  private observeEditorScrollOut() {
    const toolbarHeight = this.toolbarContainer.nativeElement.scrollHeight;

    this.editorScrollsOutObs = new IntersectionObserver(
      entries => {
        const toolbarNoShadow = !entries[0].isIntersecting;
        this.toolbarNoShadow.set(toolbarNoShadow);
      },
      {
        rootMargin: `0px 0px -${toolbarHeight + 4}px 0px`,
        threshold: 0,
      },
    );
    this.editorScrollsOutObs.observe(this.editorElement.nativeElement);
  }

  private cleanObservers() {
    if (this.toolbarPinnedObs) {
      this.toolbarPinnedObs.disconnect();
    }
    if (this.editorScrollsOutObs) {
      this.editorScrollsOutObs.disconnect();
    }
  }
}
